import styled, { css, keyframes } from 'styled-components';
import { UiColors } from 'assets/styles/old/global';

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

export const Count = styled.div`
  font-size: 14px;
  color: ${UiColors.dimGreyFillColor};
`;

/* A linked note (`?note=<id>`) lights up in the brand peach and settles back to white. */
const linkedFade = keyframes`
  from { background: ${UiColors.peachColor}; }
  to { background: ${UiColors.fillWhiteColor}; }
`;

/* One row per note inside the shared MediaCard, the same divider rhythm as the media visit rows. */
export const Item = styled.article`
  display: flex;
  gap: 14px;
  padding: 16px 20px;
  border-bottom: 1px solid ${UiColors.strokeColor};
  background: ${UiColors.fillWhiteColor};
  scroll-margin-top: 120px;

  &:last-child {
    border-bottom: none;
  }

  ${({ $highlighted }) =>
    $highlighted &&
    css`
      /* The marker stays after the fade, so the note is still findable once the colour is gone. */
      box-shadow: inset 3px 0 0 ${UiColors.mangoColor};
      animation: ${linkedFade} 2.5s ease-out 0.3s both;

      &:focus {
        outline: none;
      }

      &:focus-visible {
        outline: 2px solid var(--dodger-blue);
        outline-offset: -2px;
      }

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

export const Avatar = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${UiColors.forestColor};
  color: ${UiColors.peachColor};
  font-size: 13px;
  font-weight: 700;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const Main = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Header = styled.header`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  /* Vertically centres the 13px meta and 12px badge against the 15px name. */
  min-height: 24px;
  font-size: 13px;
  color: ${UiColors.forestColor};
`;

/* Type scale inside a note: the name, the reason and the text are all 15px and told apart by weight
   (700 / 600 / 400) — the convention of comment threads (Slack, GitHub, Linear), where the author is
   never smaller than what they wrote. Only the secondary details (date, visit link, badge) step down. */
export const Author = styled.strong`
  font-size: 15px;
  font-weight: 700;
`;

export const Muted = styled.span`
  font-size: 13px;
  color: ${UiColors.dimGreyFillColor};
`;

/* Visit link: the app's link treatment (see MediaVisitRow's DateLink) — underlined, because the link
   colour alone is ~3.8:1 on white. */
export const VisitLink = styled.a`
  font-size: 13px;
  color: var(--link-color);
  text-decoration: underline;

  &:hover,
  &:focus-visible {
    text-decoration-thickness: 2px;
  }
`;

/* Same pill as the retailer-view media rows (MediaVisitRow.styles). */
export const VisitTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;

  ${({ tone }) => {
    if (tone === 'edu')
      return css`
        background: ${UiColors.lightGreen};
        color: ${UiColors.green};
      `;
    if (tone === 'assess')
      return css`
        background: ${UiColors.peachColor};
        color: ${UiColors.orangeColor};
      `;
    return css`
      background: ${UiColors.milk};
      color: ${UiColors.errorColor};
    `;
  }}
`;

/* Reads as the note's title, so it sits at the text's size, set apart by weight alone. Free text like
   the body, so a long unbroken token breaks here too instead of running out under the actions. */
export const Reason = styled.div`
  font-size: 15px;
  line-height: 1.4;
  font-weight: 600;
  color: ${UiColors.forestColor};
  overflow-wrap: anywhere;
`;

/* Notes are free text typed on a phone in the field — keep their line breaks, and break long tokens
   (URLs) instead of widening the card. The reading text of the section, so it is the largest type in
   it, with a comfortable measure (~80 characters) instead of running the full width of a wide screen. */
export const Body = styled.p`
  margin: 0;
  max-width: 80ch;
  font-size: 15px;
  line-height: 1.6;
  color: ${UiColors.forestColor};
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  /* A long note folds to BODY_PREVIEW_LINES (NoteItem) so it doesn't push the rest of the list a
     screenful down; "Show more" opens it. */
  ${({ $clamped, $lines }) =>
    $clamped &&
    css`
      display: -webkit-box;
      -webkit-line-clamp: ${$lines};
      -webkit-box-orient: vertical;
      overflow: hidden;
    `}
`;

/* "Show more / Show less" under a folded note: a quiet text action in the link colour, like the
   Retailer List's notes toggle. */
export const MoreToggle = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 28px;
  margin-left: -8px;
  padding: 0 8px;
  border: none;
  border-radius: 4px;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: var(--link-color);
  cursor: pointer;

  &:hover {
    background: ${UiColors.cloudColor};
  }

  &:focus-visible {
    outline: 2px solid var(--dodger-blue);
    outline-offset: 2px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const Actions = styled.div`
  flex: 0 0 auto;
  display: flex;
  gap: 4px;
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: ${UiColors.dimGreyFillColor};
  cursor: pointer;

  &:hover {
    border-color: ${UiColors.strokeColor};
    color: ${UiColors.forestColor};
  }

  &:focus-visible {
    outline: 2px solid var(--dodger-blue);
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const MediaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 2px;
`;

export const Thumb = styled.a`
  display: block;
  width: 72px;
  height: 72px;
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 4px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &:focus-visible {
    outline: 2px solid var(--dodger-blue);
    outline-offset: 2px;
  }
`;

export const FileChip = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 220px;
  padding: 4px 10px;
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 999px;
  font-size: 13px;
  color: var(--link-color);
  text-decoration: underline;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    flex: 0 0 auto;
    width: 13px;
    height: 13px;
  }
`;

/* The add / edit form. Inside a note row it replaces the body; above the list it is its own card. */
export const Editor = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const EditorCard = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 6px;
  background: ${UiColors.fillWhiteColor};
`;

export const EditorActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const Empty = styled.div`
  padding: 64px 16px;
  text-align: center;
  color: ${UiColors.dimGreyFillColor};
  font-size: 14px;
  font-style: italic;
`;
